import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaChevronDown, FaCode, FaMobile, FaChartLine, FaBriefcase, FaGlobe, FaHashtag, FaArrowRight } from "react-icons/fa";
import { Link } from "react-scroll";

const services = [
  { id: "web", icon: <FaCode />, title: "Web Development", desc: "React.js & Node.js powered apps", color: "#f59e0b" },
  { id: "mobile", icon: <FaMobile />, title: "Mobile App Development", desc: "Flutter — Android & iOS", color: "#d97706" },
  { id: "marketing", icon: <FaChartLine />, title: "Digital Marketing", desc: "SEO, Ads & Email Campaigns", color: "#ef4444" },
  { id: "strategy", icon: <FaBriefcase />, title: "Business Strategy", desc: "Plan, Execute & Scale", color: "#f59e0b" },
  { id: "domain", icon: <FaGlobe />, title: "Domain & Hosting", desc: "Domain, SSL & Business Email", color: "#10b981" },
  { id: "social", icon: <FaHashtag />, title: "Social Media Management", desc: "Content, Ads & Growth", color: "#fbbf24" },
];

const marqueeItems = [
  "🚀 Launching 2026 — Dream Gold Trading",
  "💻 Web Development with React.js & Node.js",
  "📱 Flutter Apps for Android & iOS",
  "📊 Digital Marketing — SEO, Google Ads & Meta Ads",
  "🌐 Domain Registration & Web Hosting",
  "📣 Social Media Management & Growth",
  "💼 Business Strategy & Consulting",
  "🎯 Serving Clients Across India & Globally",
  "⚡ Fast Delivery — Agile Development Process",
  "🔒 Secure & Scalable Solutions",
  "🤝 Trusted by Startups & Enterprises",
  "📞 Free Consultation — Contact Us Today",
];

const navLinks = [
  { name: "Home", to: "home" },
  { name: "About", to: "about" },
  { name: "Portfolio", to: "portfolio" },
  { name: "Team", to: "team" },
  { name: "Testimonials", to: "testimonials" },
  { name: "Contact", to: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 768) setMenuOpen(false); };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setServicesOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`transition-all duration-300 ${scrolled ? "glass shadow-lg shadow-black/20 py-3" : "py-4"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          {/* Logo */}
          <motion.div whileHover={{ scale: 1.05 }} className="cursor-pointer flex items-center gap-2 flex-shrink-0">
            <Link to="home" smooth={true} duration={600} offset={-80} className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer">
              <img src="/DreamGoldLogo.png" alt="Dream Gold Trading" className="h-9 w-9 object-contain rounded-full ring-2 ring-yellow-500/30" />
              <div>
                <h3 className="text-white font-bold text-xl">Dream Gold</h3>
                <p className="text-yellow-400 text-sm font-medium">Trading</p>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-5 lg:gap-7">
            <Link to="home" spy="true" smooth="true" duration={600} offset={-80}
              className="nav-link text-gray-300 hover:text-white cursor-pointer text-sm font-medium transition-colors"
              activeclass="text-white">
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="nav-link text-gray-300 hover:text-white cursor-pointer text-sm font-medium transition-colors flex items-center gap-1"
                onClick={() => setServicesOpen(!servicesOpen)}
              >
                Services
                <motion.span animate={{ rotate: servicesOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <FaChevronDown className="text-xs" />
                </motion.span>
              </button>

              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-14 w-[540px]"
                  >
                  <div className="glass rounded-2xl p-4 shadow-2xl shadow-black/40 border border-white/10" style={{background:"rgba(10,15,30,0.98)"}}>
                    {/* Dropdown header */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
                      <div>
                        <div className="text-white font-bold text-sm">Our Services</div>
                        <div className="text-gray-400 text-xs">End-to-end technology solutions</div>
                      </div>
                      <Link to="services" smooth="true" duration={600} offset={-80} onClick={() => setServicesOpen(false)}>
                        <span className="text-yellow-400 text-xs flex items-center gap-1 hover:text-yellow-300 cursor-pointer transition-colors">
                          View All <FaArrowRight className="text-xs" />
                        </span>
                      </Link>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {services.map((s, i) => (
                        <motion.div
                          key={s.title}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.04 }}
                          onClick={() => {
                            setServicesOpen(false);
                            // Immediately dispatch event to open modal
                            window.dispatchEvent(new CustomEvent('openServiceModal', {
                              detail: { serviceId: s.id }
                            }));
                            // Also scroll to services section
                            setTimeout(() => {
                              document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
                            }, 100);
                          }}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 cursor-pointer group transition-all"
                        >
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                            style={{ background: `${s.color}20`, color: s.color }}>
                            {s.icon}
                          </div>
                          <div>
                            <div className="text-white text-xs font-semibold group-hover:text-yellow-400 transition-colors">{s.title}</div>
                            <div className="text-gray-500 text-xs">{s.desc}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <Link to="#" smooth="true" duration={600} offset={-80} onClick={() => setServicesOpen(false)}>
                        <div className="gradient-bg rounded-xl p-3 flex items-center justify-between cursor-pointer hover:opacity-90 transition-opacity">
                          <div>
                            <div className="text-white text-xs font-bold">Ready to start a project?</div>
                            <div className="text-white/70 text-xs">Get a free consultation today</div>
                          </div>
                          <FaArrowRight className="text-white text-sm" />
                        </div>
                      </Link>
                    </div>
                  </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(1).map((link) => (
              <Link key={link.name} to={link.to} spy="true" smooth="true" duration={600} offset={-80}
                className="nav-link text-gray-300 hover:text-white cursor-pointer text-sm font-medium transition-colors"
                activeclass="text-white">
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="contact" smooth="true" duration={600} offset={-80}>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="btn-primary px-5 py-2.5 rounded-full text-white text-sm font-semibold">
                <span>Get Started</span>
              </motion.button>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button className="md:hidden text-white text-xl p-2 glass rounded-lg"
            onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <AnimatePresence mode="wait">
              {menuOpen
                ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}><FaTimes /></motion.span>
                : <motion.span key="bars" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}><FaBars /></motion.span>
              }
            </AnimatePresence>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden glass border-t border-white/10 overflow-hidden"
            >
              <div className="px-4 py-3 flex flex-col gap-1">
                <Link to="home" spy smooth duration={600} offset={-80}
                  className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer text-sm font-medium py-3 px-3 rounded-lg transition-all"
                  onClick={() => setMenuOpen(false)}>
                  Home
                </Link>

                {/* Mobile Services Accordion */}
                <div>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="w-full text-left text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer text-sm font-medium py-3 px-3 rounded-lg transition-all flex items-center justify-between"
                  >
                    Services
                    <motion.span animate={{ rotate: mobileServicesOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <FaChevronDown className="text-xs" />
                    </motion.span>
                  </button>
                  <AnimatePresence>
                    {mobileServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden ml-3 mt-1"
                      >
                        <div className="grid grid-cols-2 gap-2 pb-2">
                          {services.map((s) => (
                            <div
                              key={s.title}
                              onClick={() => {
                                setMenuOpen(false);
                                setMobileServicesOpen(false);
                                // Immediately dispatch event to open modal
                                window.dispatchEvent(new CustomEvent('openServiceModal', {
                                  detail: { serviceId: s.id }
                                }));
                                // Also scroll to services section
                                setTimeout(() => {
                                  document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
                                }, 100);
                              }}
                              className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 cursor-pointer transition-all"
                            >
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs"
                                style={{ background: `${s.color}20`, color: s.color }}>
                                {s.icon}
                              </div>
                              <span className="text-gray-300 text-xs">{s.title}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {navLinks.slice(1).map((link) => (
                  <Link key={link.name} to={link.to} spy smooth duration={600} offset={-80}
                    className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer text-sm font-medium py-3 px-3 rounded-lg transition-all"
                    onClick={() => setMenuOpen(false)}>
                    {link.name}
                  </Link>
                ))}

                <Link to="contact" smooth duration={600} onClick={() => setMenuOpen(false)}>
                  <button className="btn-primary w-full py-3 rounded-xl text-white font-semibold mt-2 text-sm">
                    <span>Get Started</span>
                  </button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Marquee Ticker */}
      <div className="bg-gradient-to-r from-yellow-600/20 via-amber-600/20 to-yellow-600/20 border-b border-white/10 backdrop-blur-sm overflow-hidden py-2">
        <div className="marquee-track flex gap-0">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="marquee-item text-xs font-medium text-gray-300 whitespace-nowrap px-6 flex items-center gap-2">
              <img src="/DreamGoldLogo.png" alt="" className="w-4 h-4 rounded-full object-contain opacity-70 flex-shrink-0" />
              {item}
              <span className="text-yellow-500/50 mx-1">•</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
